// The single QueryClient for the application.
//
// Kept in its own module so both the router (for `beforeLoad` guards) and the
// React tree can use the same instance without an import cycle.

import { MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import { refreshInterval } from '@/query/refresh';

export const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    // A conflict means someone else changed the data: fetch the current
    // version at once, so the screen shows it without the user reloading.
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'conflict') {
        void queryClient.invalidateQueries({ queryKey: ['account'] });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      gcTime: 5 * 60_000,
      // Retry only transient failures; never retry an auth or validation error.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && !error.isRetryable) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Data keeps itself current while on screen (see refresh.ts).
      refetchInterval: (query) => refreshInterval(query.queryKey),
    },
    mutations: {
      // There is no idempotency guarantee on the wire, so a retry could
      // duplicate a mutation. Never retry automatically.
      retry: false,
    },
  },
});
